import { MediaObjectUpload, MediaObjectUrl } from '@kudos/http-client'
import Stack from '@mui/material/Stack'
import AttachmentUploader from '@shared/components/mediaUploads/AttachmentUploader'
import ErrorText from '@shared/forms/ErrorText'
import { UseControllerProps, useController, useFieldArray } from 'react-hook-form'
import type { FieldValues, Path, UseFormClearErrors, UseFormSetError } from 'react-hook-form'
import { AttachmentType } from '../hooks/useFileMediaUpload'

type AttachmentFieldProps = UseControllerProps & {
  file?: File | null
  index: number
  isDeleteDisabled?: boolean
  onError?: (error: Error) => void
  onDelete?: (index: number) => void
  isUploadDisabled?: boolean
  attachmentSrc?: MediaObjectUrl
  attachmentType: AttachmentType
  mutationFn?: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  setFormError?: UseFormSetError<FieldValues>
  clearFormErrors?: UseFormClearErrors<FieldValues>
  apiErrorFieldPaths?: Path<FieldValues>[]
}

const AttachmentField = ({
  control,
  file,
  index,
  onError,
  onDelete,
  attachmentSrc,
  attachmentType,
  disabled,
  mutationFn,
  setFormError,
  clearFormErrors,
  apiErrorFieldPaths,
}: AttachmentFieldProps) => {
  const {
    field: { name },
    fieldState: { error },
  } = useController({
    control,
    name: 'attachments',
  })

  const { update } = useFieldArray({ control, name })

  const handleAttachmentUploaded = (mediaObjectId?: string, attachmentSrc?: MediaObjectUrl) => {
    if (!!mediaObjectId) {
      update(index, { mediaObjectId, attachmentType, attachmentSrc })
    }
  }

  const handleError = (err: Error) => {
    if (!!onError) onError(err)
  }

  return (
    <Stack useFlexGap spacing={3}>
      <AttachmentUploader
        index={index}
        fileAttachment={file}
        attachmentSrc={attachmentSrc}
        attachmentType={attachmentType}
        onAttachmentUploaded={handleAttachmentUploaded}
        onDelete={!!onDelete ? () => onDelete(index) : undefined}
        onError={handleError}
        disabled={disabled}
        mutationFn={mutationFn}
        setFormError={setFormError}
        clearFormErrors={clearFormErrors}
        apiErrorFieldPaths={apiErrorFieldPaths}
      />
      {!!error && <ErrorText>{error.message}</ErrorText>}
    </Stack>
  )
}

export default AttachmentField
